import { supabase } from '@/integrations/supabase/client';

const BASE = () => (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

export async function enviarSmsManualLead(leadId: string, telefone: string, mensagem: string) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada — faça login novamente.');

  const response = await fetch(`${BASE()}/v1/leads-carteira/${leadId}/sms-manual`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ telefone, mensagem }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || 'O SMS não foi enviado.');
  return body;
}

export interface DestinoProposta {
  canal: 'email' | 'sms';
  destino: string;
  fontes: string[];
  regra: 'contato_real' | 'todos_do_cnpj';
  /** Último envio para este destino (qualquer SMS do CRM / e-mail de proposta). */
  ultimo_envio_em?: string | null;
  ultimo_envio_por?: string | null;
}

const SETE_DIAS_MS = 7 * 24 * 60 * 60 * 1000;
/** SMS para o mesmo celular fica travado por 7 dias (a trava mora no envio). */
export function smsTravado(d: DestinoProposta): boolean {
  return d.canal === 'sms' && !!d.ultimo_envio_em && Date.now() - new Date(d.ultimo_envio_em).getTime() < SETE_DIAS_MS;
}

async function chamar(caminho: string, init: RequestInit = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada — faça login novamente.');
  const response = await fetch(`${BASE()}${caminho}`, {
    ...init,
    headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || 'Não foi possível concluir.');
  return body;
}

/** Para quem a proposta vai: a régua fn_destinos_proposta_lead, no banco. */
export async function buscarDestinosProposta(leadId: string): Promise<{ emails: DestinoProposta[]; telefones: DestinoProposta[] }> {
  return chamar(`/v1/leads-carteira/${leadId}/destinos-proposta`);
}

export async function enviarSmsPropostaLead(leadId: string, telefones: string[], mensagem: string, modelo: string | null) {
  return chamar(`/v1/leads-carteira/${leadId}/sms-proposta`, {
    method: 'POST',
    body: JSON.stringify({ telefones, mensagem, modelo }),
  }) as Promise<{ enviados: string[]; falhas: Array<{ telefone: string; erro: string }>; ja_receberam?: string[] }>;
}

export interface PainelSms {
  sms: number;
  disparos: number;
  chegaram: number;
  nao_chegaram: number;
  empresas_entregues: number;
  chamaram: number;
  quem_envia: Array<{ nome: string | null; cargo: string | null; usuario_id: string | null; sms: number; chegou: number; nao_chegou: number }>;
  aproveitamento: Array<{ nome: string; usuario_id: string; disparos: number; entregues: number; chamou: number }>;
}

export interface EmpresaSmsPainel {
  cnpj: string;
  razao_social: string | null;
  enviado_em: string;
  sms: number;
  chegou: number;
  chamou_em: string | null;
  conversa_id: string | null;
  mensagem: string | null;
}

export async function buscarPainelSms(dias: number, equipeId: string | null): Promise<PainelSms> {
  const p = new URLSearchParams({ dias: String(dias) });
  if (equipeId) p.set('equipe_id', equipeId);
  return (await chamar(`/v1/sms/painel?${p}`)).data as PainelSms;
}

export async function buscarEmpresasPainelSms(dias: number, equipeId: string | null, usuarioId: string, papel: 'dono' | 'enviou'): Promise<EmpresaSmsPainel[]> {
  const p = new URLSearchParams({ dias: String(dias), usuario_id: usuarioId, papel });
  if (equipeId) p.set('equipe_id', equipeId);
  return (await chamar(`/v1/sms/painel/empresas?${p}`)).data as EmpresaSmsPainel[];
}

export type SituacaoEnvioProposta = 'saindo' | 'enviada' | 'entregue' | 'lida' | 'nao_chegou';

export interface StatusEnvioProposta {
  destino: string | null;
  linhas: Array<{ papel: 'primaria' | 'secundaria' | 'reserva'; nome: string; telefone: string | null; no_ar: boolean; usada: boolean }>;
  sai_por: { nome: string; motivo: 'conversa' | 'primaria' | 'secundaria' | 'reserva' | 'oficial' } | null;
  ultimo: {
    enviada_em: string;
    situacao: SituacaoEnvioProposta;
    erro: string | null;
    linha: string | null;
    igual_atual: boolean;
    cliente_respondeu: boolean;
  } | null;
  bloqueio: { codigo: string; mensagem: string } | null;
  pode_criar_pedido: boolean;
  codigo_criar?: string | null;
}

/** Por qual linha a proposta sai, o que houve com o último envio e se o Criar pedido já pode. */
export async function buscarEnvioProposta(leadId: string): Promise<StatusEnvioProposta> {
  const body = await chamar(`/v1/leads-carteira/${leadId}/envio-proposta`);
  return body.data as StatusEnvioProposta;
}
